/* eslint-disable react/no-danger */

import { Button, STYLE_FILL, TYPE_LINK } from '@eventbrite/eds-button';
import { gettext } from '@eventbrite/i18n';
import { css, cx } from 'emotion';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { default as sanitize } from 'sanitize-html';
import {
    GA_ACTION_VIEW_WEBINAR_LINK,
    HTML_SANITIZER_CONFIG,
} from '../../constants';
import {
    getVimeoEmbedUrl,
    isEmbedUrl,
    isVimeoShowcaseEmbedUrl,
} from '../../utils';
import { EmbedWithFallbackContent } from './EmbedWithFallbackContent';
import { moduleBorderStyle } from './moduleBorderStyle';
import { Timer } from './Timer';
import { useVideoProvider } from './useVideoProvider';

interface Props {
    data: {
        image?: { url: string };
        webinarUrl?: { url: string };
        instructions?: { text: string };
    };
    event: any;
    onClick: (event: any, GAEvent: string, url?: string) => void;
}

export const WebinarModule: React.FC<Props> = ({ data, event, onClick }) => {
    const { renderIcon } = useVideoProvider(data?.webinarUrl?.url);
    const imageUrl = data?.image?.url || '';
    const webinarUrl = data?.webinarUrl?.url || '';
    const instructionsText = data?.instructions?.text || '';
    const action = onClick(event, GA_ACTION_VIEW_WEBINAR_LINK, webinarUrl);
    const icon = renderIcon();
    const __html = sanitize(instructionsText, HTML_SANITIZER_CONFIG);
    const isVimeoShowcase = isVimeoShowcaseEmbedUrl(webinarUrl);
    const [vimeoShowcaseEmbedUrl, setVimeoShowcaseEmbedUrl] =
        useState(webinarUrl);

    useEffect(() => {
        if (isVimeoShowcase) {
            getVimeoEmbedUrl(webinarUrl).then((url) => {
                setVimeoShowcaseEmbedUrl(url);
            });
        }
    }, [webinarUrl, isVimeoShowcase]);

    interface RenderEmbedWithFallbackContentProps {
        webinarUrl: string;
        vimeoShowcaseEmbedUrl: string;
        imageUrl: string;
        action: void;
        isVimeoShowcase: boolean;
    }

    const renderEmbedWithFallbackContent: React.FC<
        RenderEmbedWithFallbackContentProps
    > = ({
        webinarUrl,
        vimeoShowcaseEmbedUrl,
        imageUrl,
        action,
        isVimeoShowcase,
    }) => {
        if (isEmbedUrl(webinarUrl) && !isVimeoShowcase) {
            return (
                <EmbedWithFallbackContent
                    url={webinarUrl}
                    imageUrl={imageUrl}
                    action={action}
                />
            );
        }
        if (isVimeoShowcase) {
            return (
                <EmbedWithFallbackContent
                    url={vimeoShowcaseEmbedUrl}
                    imageUrl={imageUrl}
                    action={action}
                    shouldEmbed={isVimeoShowcase}
                />
            );
        }
        if (imageUrl) {
            return (
                <EmbedWithFallbackContent
                    url={webinarUrl}
                    imageUrl={imageUrl}
                    action={action}
                />
            );
        }
        return null;
    };

    return (
        <div
            className={cx(
                'dc-modules__item--webinar',
                moduleBorderStyle,
                'eds-l-pad-all-6',
            )}
        >
            <div className={cx(styles.title, 'eds-l-mar-bot-6')}>
                {icon && <span className="eds-l-mar-right-3">{icon}</span>}

                <h2 className="eds-text-bl">
                    {get(data, 'webinarUrl.text', '')}
                </h2>

                <a
                    href={webinarUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="eds-l-mar-left-3"
                >
                    <Timer start={event.start.utc} end={event.end.utc} />
                </a>
            </div>

            <div className="eds-l-mar-top-2 dc-modules__item--webinar__content">
                {renderEmbedWithFallbackContent({
                    webinarUrl,
                    vimeoShowcaseEmbedUrl,
                    imageUrl,
                    action,
                    isVimeoShowcase,
                })}
            </div>
            <div
                data-spec="dc-modules__item--instructions"
                className="dc-modules__item--instructions"
                dangerouslySetInnerHTML={{ __html }}
            />
            {!isVimeoShowcase && (
                <>
                    <div
                        className={cx(
                            styles.separator,
                            'eds-l-mar-top-6',
                            'eds-l-mar-bot-6',
                        )}
                    ></div>
                    <Button
                        __containerClassName={styles.button}
                        style={STYLE_FILL}
                        type={TYPE_LINK}
                        onClick={() => window.open(webinarUrl)}
                    >
                        {gettext('Join now')}
                    </Button>
                </>
            )}
        </div>
    );
};

const styles = {
    title: css`
        display: flex;
        align-items: center;
    `,
    separator: css`
        border-bottom: 1px solid #eeedf2;
    `,
    button: css`
        min-width: 120px;
    `,
};
